import { CONFIG, REGEX_PATTERNS, HTML_TEMPLATES, escapeHtml } from './constants.js';

/**
 * Converts a subset of Markdown syntax to HTML for display in the terminal blog viewer.
 * Handles headers (H2/H3), bullets, fenced/inline code, bold, italic, and links.
 * H1 titles are intentionally suppressed as they are rendered separately as styled headers.
 *
 * @param {string|null|undefined} text - Raw Markdown content
 * @returns {string} HTML string safe for use with innerHTML
 */
export function formatMarkdown(text) {
    if (!text) return CONFIG.EMPTY;

    const escaped = escapeHtml(text);

    // Process line-by-line transformations first
    const formattedLines = escaped.split(CONFIG.NEWLINE).map(line => {
        const trimmed = line.trim();

        // Match YouTube URL to render as embedded video
        const ytMatch = trimmed.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s\<\>]+(?:\/|v\/|embed\/)|watch\?(?:[^\/\n\s\<\>]+&amp;)*v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/i);
        if (ytMatch) {
            return HTML_TEMPLATES.BLOG_VIDEO(ytMatch[1]);
        }

        // Suppress H1 titles — rendered separately as stylized headers
        if (trimmed.startsWith(CONFIG.MARKDOWN_H1_PREFIX) && !trimmed.includes(CONFIG.MARKDOWN_H1_INCLUSION)) {
            return CONFIG.EMPTY;
        }

        if (trimmed.startsWith(CONFIG.MARKDOWN_H3_PREFIX)) {
            return HTML_TEMPLATES.H3(trimmed.substring(4));
        }
        if (trimmed.startsWith(CONFIG.MARKDOWN_H2_PREFIX)) {
            return HTML_TEMPLATES.H2(trimmed.substring(3));
        }

        // Bullet points (both `*` and `-` flavours)
        if (trimmed.startsWith(CONFIG.MARKDOWN_BULLET_STAR) || trimmed.startsWith(CONFIG.MARKDOWN_BULLET_DASH)) {
            return HTML_TEMPLATES.BULLET(trimmed.substring(2));
        }

        return line;
    });

    // Apply block and inline regex transformations in a defined order
    return formattedLines.join(CONFIG.NEWLINE)
        // Fenced code blocks (language-tagged variant must come first)
        .replace(REGEX_PATTERNS.MARKDOWN_CODE_JAVA, (_, code) => HTML_TEMPLATES.CODE_BLOCK(code))
        .replace(REGEX_PATTERNS.MARKDOWN_CODE_BLOCK, (_, code) => HTML_TEMPLATES.CODE_BLOCK(code))
        // Inline code
        .replace(REGEX_PATTERNS.MARKDOWN_INLINE_CODE, (_, code) => HTML_TEMPLATES.INLINE_CODE(code))
        // Bold before italic to avoid partial matches
        .replace(REGEX_PATTERNS.MARKDOWN_BOLD, (_, bold) => HTML_TEMPLATES.BOLD(bold))
        .replace(REGEX_PATTERNS.MARKDOWN_ITALIC, (_, italic) => HTML_TEMPLATES.ITALIC(italic))
        // Markdown links
        .replace(REGEX_PATTERNS.MARKDOWN_LINK, (_, text, url) => HTML_TEMPLATES.LINK(text, url))
        // Newlines → <br>
        .replace(REGEX_PATTERNS.NEWLINE, HTML_TEMPLATES.BR);
}
